/**
 * Furniture drawn *over art the app does not own*.
 *
 * The studio's chrome can answer for a surface because the app decides what is
 * behind it — see `chrome.ts`. Two places cannot be answered that way, because
 * what is behind them belongs to the creator:
 *
 * - the **cover**, where the studio's own pills and title sit on the creator's
 *   picture;
 * - the **video**, where the editor's own controls sit on a frame of the
 *   creator's clip, and the video's ground shows through around it.
 *
 * A picture is neither light nor dark by the app's appearance, so these are
 * deliberately independent of Settings → Appearance: the treatment is *glasses
 * over a photograph*, and it has to read on a bright sky and a night shot alike.
 * That is also why they are not chrome roles — a role would have to know the
 * appearance, and here the appearance is the wrong question.
 *
 * They live here, once, rather than inline at each use site. That is what lets
 * `no-colour-literals` be enforced on the screens themselves: a screen has no
 * business naming a colour, and the two honest exceptions are a *declared*
 * module like this one, or a palette that genuinely is the data (see
 * `lib/slideTokens.ts`).
 *
 * What does **not** belong here:
 *
 * - anything the app can answer for — the studio's surfaces, ink and hairlines
 *   are chrome roles;
 * - the over-art roles ALF already defines (`onCanvas`, `canvasBorder`), which
 *   stay on the chrome;
 * - the *book's* own colours, which belong to a theme (`themes-flashbook/`).
 */
import {utils} from '@bsky.app/alf'

export const OVER_ART = {
  // ── Scrims ──────────────────────────────────────────────────────────────
  /** The top of a cover, darkened so the mode pills read against it. */
  scrimFrom: 'rgba(3,7,16,0.8)',
  /** The other end of that gradient: the picture, untouched. */
  scrimClear: 'rgba(3,7,16,0)',
  /**
   * The bottom of a cover, in three stops, so the title and description always
   * have a floor to sit on however bright the picture is.
   */
  titleScrim: ['rgba(3,7,16,0)', 'rgba(3,7,16,0.72)', 'rgba(3,7,16,0.96)'],
  /** A caption over a *playing* video: darker, because a frame moves. */
  captionScrim: ['transparent', 'rgba(0,0,0,0.92)'],
  /** The ground a video sits on, so a letterboxed frame is not raw studio. */
  videoMatte: '#050d18',
  /** The same ground as a gradient: a flat fill reads as a hole, this reads as depth. */
  videoMatteGradient: ['#050d18', '#0a1220', '#060e1a'],
  /** Top and bottom of the wash over a video, so the editor's controls read. */
  videoWashTop: 'rgba(0,0,0,0.3)',
  videoWashBottom: 'rgba(0,0,0,0.5)',

  // ── The cover trimmer ───────────────────────────────────────────────────
  //
  // Three values for the film strip, and they are shadow rather than a scrim on
  // purpose: what is under them is a photograph, and a photograph is dimmed, not
  // tinted with the app's paper. The chrome's scrim is a *light* veil in the light
  // scheme, so over a dark film it would wash the frames being discarded into a
  // pale haze — which reads as "the strip is broken" rather than as "these seconds
  // are not being kept".
  /** What the parts of the film being cut are dimmed with. */
  trimVeil: 'rgba(6,10,16,0.66)',
  /**
   * What the kept frames are *lifted* with.
   *
   * Almost nothing, and deliberately: enough to separate the kept window from the
   * film around it when the film is as black as the veil, and not enough to tint
   * the frames — the frames are the thing being judged.
   */
  trimLift: 'rgba(255,255,255,0.06)',
  /**
   * What shows through the hairline between two frames.
   *
   * Explicit rather than "whatever the panel paints", which is what it was: the
   * gaps let the sheet's own fill through, so the separators were bright white in
   * the light scheme and near-black in the dark one — over the *same* film, the
   * strip looked like two different controls depending on the app's setting.
   */
  trimSeam: 'rgba(255,255,255,0.14)',

  // ── Ink over art ────────────────────────────────────────────────────────
  /**
   * The shadow under text that sits on a picture. Every cover uses the same
   * one: a soft drop shadow is what makes white ink legible on an arbitrary
   * image, and it is cheaper than a scrim behind each line.
   */
  inkShadow: 'rgba(0,0,0,0.65)',
  /**
   * Over-art ink, quietest first.
   *
   * Three near-identical alphas used to be spelled out separately (0.55, 0.6 and
   * 0.72) for the same job in different corners of the cover step. Two of those
   * are now one: the difference is below what a person can see, and a palette
   * with five whites in it is how the real one gets lost.
   */
  inkFaint: 'rgba(255,255,255,0.6)',
  inkMuted: 'rgba(255,255,255,0.72)',
  inkStrong: 'rgba(255,255,255,0.9)',

  // ── Controls over art ───────────────────────────────────────────────────
  /**
   * A pill or a panel on the cover: translucent black, so the picture still
   * shows through, with a hairline edge to give it a shape.
   */
  panel: 'rgba(0,0,0,0.42)',
  panelBorder: 'rgba(255,255,255,0.14)',
  /**
   * The same panel over a video, where something is *moving* behind it: a little
   * more opaque and a firmer edge, for the reason `captionScrim` is darker than
   * `titleScrim`.
   */
  panelOnVideo: 'rgba(0,0,0,0.55)',
  panelOnVideoBorder: 'rgba(255,255,255,0.2)',
  /**
   * The "renders in HTML" marker that floats over a *theme's* thumbnail in the
   * theme picker. Like the rest of this module it sits on art the app does not
   * own — a miniature of the book — so it cannot be a chrome surface; it is a
   * marker, and it is teal to be unmistakably not one of the theme's own colours.
   */
  badgeOnArt: 'rgba(20,184,166,0.85)',
} as const

/**
 * The accent as a *tint* over art — the wash behind a selected pill.
 *
 * A function rather than a value, because the hue is not ours to name: it is the
 * app's accent, so it stays a chrome role and only the *alpha* is furniture. That
 * is the difference between a tint and a colour of its own.
 */
export function accentTintOverArt(accent: string): string {
  return utils.alpha(accent, 0.28)
}
